<template>
  <v-card
    id="screenContent"
    class="secondary vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header secondary" ref="screenHeader">
      <vetalfa-small-header
        v-if="!_isMobile()"
        showBreadCrumbAndTitle
        :title="conversationTitle"
        :breadcrumb="conversationTitle"
      ></vetalfa-small-header>
      <!-- Mobile header -->
      <v-row v-if="_isMobile()" no-gutters align="center" class="secondary chat-mobile-header-row px-4 pb-2">
        <v-btn icon small @click="routeBack()">
          <v-icon color="primary">{{ mdiChevronLeft }}</v-icon>
        </v-btn>
        <div class="chat-mobile-header-icon ml-2">
          <v-icon color="primary" size="16">{{ mdiCreation }}</v-icon>
        </div>
        <span class="subtitle-2 font-weight-bold primary--text ml-2 text-truncate" style="max-width: 60%">{{ conversationTitle }}</span>
      </v-row>
    </div>
    <div
      ref="screenContent"
      style="width: 100%"
      @touchstart="onPullStart"
      @touchmove="onPullMove"
      @touchend="onPullEnd"
    >
      <!-- Pull to refresh indicator -->
      <div
        class="chat-pull-indicator"
        :style="{ height: pullDistance + 'px', opacity: pullDistance > 10 ? 1 : 0 }"
      >
        <v-progress-circular
          v-if="refreshing"
          indeterminate
          size="24"
          width="2"
          color="primary"
        ></v-progress-circular>
        <v-icon
          v-else
          :style="{ transform: pullDistance > 60 ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }"
          color="grey"
          size="24"
        >{{ mdiChevronDown }}</v-icon>
      </div>
      <v-row no-gutters justify="center" class="secondary">
        <v-col
          cols="12"
          class="ma-0 pa-0 px-4 pt-4"
          style="max-width: 1000px"
        >
          <!-- Skeleton -->
          <div v-if="initialLoading">
            <div class="d-flex justify-end mb-4">
              <div class="chat-skeleton-bubble chat-skeleton-bubble--user">
                <div class="chat-skeleton-bar" style="width: 80%; height: 12px; margin-bottom: 6px"></div>
                <div class="chat-skeleton-bar" style="width: 50%; height: 12px"></div>
              </div>
            </div>
            <div class="d-flex justify-start mb-4">
              <div class="chat-skeleton-avatar"></div>
              <div class="chat-skeleton-bubble chat-skeleton-bubble--assistant ml-2">
                <div class="chat-skeleton-bar" style="width: 90%; height: 12px; margin-bottom: 6px"></div>
                <div class="chat-skeleton-bar" style="width: 70%; height: 12px; margin-bottom: 6px"></div>
                <div class="chat-skeleton-bar" style="width: 40%; height: 12px"></div>
              </div>
            </div>
            <div class="d-flex justify-end mb-4">
              <div class="chat-skeleton-bubble chat-skeleton-bubble--user">
                <div class="chat-skeleton-bar" style="width: 60%; height: 12px"></div>
              </div>
            </div>
            <div class="d-flex justify-start mb-4">
              <div class="chat-skeleton-avatar"></div>
              <div class="chat-skeleton-bubble chat-skeleton-bubble--assistant ml-2">
                <div class="chat-skeleton-bar" style="width: 85%; height: 12px; margin-bottom: 6px"></div>
                <div class="chat-skeleton-bar" style="width: 60%; height: 12px"></div>
              </div>
            </div>
          </div>

          <!-- Welcome state -->
          <div
            v-if="!initialLoading && isNew && !messages.length"
            class="chat-welcome"
          >
            <div class="chat-welcome-avatar">
              <v-icon color="primary" size="28">{{ mdiCreation }}</v-icon>
            </div>
            <div class="text-h6 font-weight-bold primary--text mt-3">
              Como posso te ajudar?
            </div>
            <div class="chat-suggestions mt-5">
              <v-chip
                v-for="(chip, i) in suggestions"
                :key="i"
                outlined
                color="primary"
                class="chat-suggestion-chip"
                @click="fillSuggestion(chip)"
              >
                {{ chip }}
              </v-chip>
            </div>
          </div>

          <!-- Messages -->
          <transition-group
            v-if="!initialLoading && messages.length"
            name="chat-bubble-enter"
            tag="div"
          >
            <div
              v-for="(msg, index) in messages"
              :key="msg.timestamp + '-' + index"
              class="mb-4"
              :class="{
                'd-flex justify-end': msg.role === 'user',
                'd-flex justify-start': msg.role !== 'user',
              }"
            >
              <!-- Assistant avatar -->
              <div
                v-if="msg.role !== 'user'"
                class="chat-ai-avatar mr-2 flex-shrink-0"
              >
                <v-icon color="primary" size="14">{{ mdiCreation }}</v-icon>
              </div>

              <!-- User bubble -->
              <div
                v-if="msg.role === 'user'"
                class="chat-bubble chat-bubble--user"
              >
                <div
                  v-if="msg.attachments && msg.attachments.length"
                  class="chat-attach-list"
                >
                  <div
                    v-for="(att, aidx) in msg.attachments"
                    :key="aidx"
                    class="chat-attach-chip"
                    :title="att.name"
                  >
                    <v-icon size="14" color="white" class="mr-1">{{ iconForMime(att.mimeType) }}</v-icon>
                    <span class="caption text-truncate">{{ att.name }}</span>
                  </div>
                </div>
                <div
                  v-if="msg.content"
                  class="subtitle-2 white--text"
                  style="white-space: pre-wrap"
                >
                  {{ msg.content }}
                </div>
                <div class="chat-timestamp chat-timestamp--user mt-1">
                  {{ formatTime(msg.timestamp) }}
                </div>
              </div>

              <!-- Assistant bubble -->
              <div
                v-else
                class="chat-bubble-group"
              >
                <div class="chat-bubble chat-bubble--assistant">
                  <div
                    v-open-links-in-new-tab="handleLinkClick"
                    class="chat-markdown"
                  >
                    <vue-markdown :source="getProcessedContent(msg)" :html="false" />
                  </div>
                  <!-- Web search badge -->
                  <div v-if="msg.did_web_search" class="chat-web-badge mt-2">
                    <v-icon x-small color="primary" class="mr-1">{{ mdiWeb }}</v-icon>
                    <span class="caption primary--text">Fontes da web</span>
                  </div>
                  <div class="chat-timestamp mt-1">
                    {{ formatTime(msg.timestamp) }}
                  </div>
                </div>
                <!-- Copy button (right side) -->
                <v-btn
                  icon
                  x-small
                  class="chat-copy-btn"
                  @click="copyMessage(msg.content)"
                >
                  <v-icon size="14" color="grey">{{ mdiContentCopy }}</v-icon>
                </v-btn>
              </div>
            </div>
          </transition-group>

          <!-- Typing indicator -->
          <div v-if="isProcessing" class="mb-4">
            <div class="d-flex justify-start">
              <div class="chat-ai-avatar mr-2 flex-shrink-0">
                <v-icon color="primary" size="14">{{ mdiCreation }}</v-icon>
              </div>
              <div class="chat-bubble chat-bubble--assistant">
                <div class="chat-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
            <div class="chat-processing-hint mt-2">
              Estamos preparando sua resposta. Fique a vontade para continuar navegando pelo app enquanto isso.
            </div>
          </div>

          <!-- Error state -->
          <div v-if="hasError" class="chat-error mb-4">
            <div class="chat-error-text">
              Seu processo nao foi concluido com sucesso para esta conversa, por favor tente novamente.
            </div>
            <v-btn
              small
              rounded
              color="error"
              class="text-none mt-2"
              @click="retryLastMessage"
            >
              Tentar novamente
            </v-btn>
          </div>

          <div ref="messagesEnd" style="height: 1px"></div>
        </v-col>
      </v-row>
      <v-row no-gutters class="transparent" style="height: 100px"></v-row>
    </div>

    <!-- Scroll to bottom FAB -->
    <v-btn
      v-show="showScrollBottom"
      fab
      x-small
      color="white"
      class="chat-scroll-bottom-fab"
      @click="scrollToBottom()"
    >
      <v-icon color="primary" size="20">{{ mdiChevronDown }}</v-icon>
    </v-btn>

    <!-- Input bar -->
    <div class="chat-input-bar">
      <div class="chat-input-wrapper">
        <div class="chat-input-card">
          <ai-attachment-picker
            v-model="files"
            :disabled="sending || isProcessing"
          />
          <v-textarea
            v-model="textModel"
            placeholder="Digite sua mensagem..."
            :loading="sending"
            :disabled="sending || isProcessing"
            auto-grow
            no-resize
            rows="1"
            row-height="14"
            class="chat-input"
            hide-details
            solo
            flat
          >
            <template v-slot:append>
              <v-btn
                :loading="sending"
                :disabled="!canSend"
                @click="sendMsg"
                fab
                color="primary"
                class="chat-send-btn"
                x-small
              >
                <v-icon color="white" size="18">{{ mdiArrowUp }}</v-icon>
              </v-btn>
            </template>
          </v-textarea>
        </div>
      </div>
    </div>

    <!-- Copy snackbar -->
    <v-snackbar v-model="copySnackbar" :timeout="1500" bottom color="primary">
      Copiado!
    </v-snackbar>
  </v-card>
</template>

<style>
/* ── Pull to refresh ── */
.chat-pull-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: opacity 0.2s ease;
}

/* ── Mobile header ── */
.chat-mobile-header-row {
  padding-top: calc(12px + env(safe-area-inset-top));
  padding-top: calc(12px + constant(safe-area-inset-top));
}
.chat-mobile-header-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Chat bubbles ── */
.chat-bubble {
  max-width: 85%;
  padding: 12px 16px;
}
.chat-bubble--user {
  background-color: #193153;
  color: white;
  border-radius: 16px 16px 4px 16px;
  box-shadow: 0 1px 4px rgba(25, 49, 83, 0.18);
}
.chat-attach-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}
.chat-attach-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 3px 10px;
  max-width: 220px;
}
.chat-attach-chip .caption {
  color: #fff;
  font-size: 12px;
}
.chat-bubble--assistant {
  background-color: white;
  border-radius: 16px 16px 16px 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Bubble group (assistant bubble + copy btn) */
.chat-bubble-group {
  display: flex;
  align-items: flex-end;
  max-width: 85%;
}
.chat-bubble-group .chat-bubble {
  max-width: 100%;
  min-width: 0;
}

/* Copy button */
.chat-copy-btn {
  opacity: 0.4;
  transition: opacity 0.2s ease;
  margin-left: 2px;
  flex-shrink: 0;
}
.chat-copy-btn:hover {
  opacity: 1;
}

/* Processing hint */
.chat-processing-hint {
  font-size: 12px;
  color: #9e9e9e;
  padding-left: 34px;
  line-height: 1.4;
}

/* Web search badge */
.chat-web-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(25, 49, 83, 0.06);
  border-radius: 12px;
  padding: 2px 10px;
}

/* AI avatar */
.chat-ai-avatar {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

/* Timestamps */
.chat-timestamp {
  font-size: 11px;
  color: #9e9e9e;
  line-height: 1;
}
.chat-timestamp--user {
  color: rgba(255, 255, 255, 0.6);
  text-align: right;
}

/* ── Scroll to bottom FAB ── */
.chat-scroll-bottom-fab {
  position: fixed !important;
  bottom: 80px;
  right: 16px;
  z-index: 99;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

/* ── Skeleton ── */
.chat-skeleton-avatar {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);
  background-size: 200% 100%;
  animation: chat-shimmer 1.5s infinite;
  margin-top: 4px;
}
.chat-skeleton-bubble {
  padding: 12px 16px;
  border-radius: 16px;
}
.chat-skeleton-bubble--user {
  background: #e0e0e0;
  border-radius: 16px 16px 4px 16px;
  max-width: 60%;
  min-width: 120px;
}
.chat-skeleton-bubble--assistant {
  background: white;
  border-radius: 16px 16px 16px 4px;
  max-width: 75%;
  min-width: 180px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.chat-skeleton-bar {
  border-radius: 4px;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);
  background-size: 200% 100%;
  animation: chat-shimmer 1.5s infinite;
}
@keyframes chat-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Bubble enter animation ── */
.chat-bubble-enter-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.chat-bubble-enter-enter {
  opacity: 0;
  transform: translateY(12px);
}

/* ── Welcome state ── */
.chat-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 16px 24px;
}
.chat-welcome-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-suggestions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  max-width: 420px;
}
.chat-suggestion-chip {
  cursor: pointer;
  white-space: normal !important;
  height: auto !important;
  min-height: 32px;
  padding: 6px 14px !important;
}
.chat-suggestion-chip .v-chip__content {
  white-space: normal !important;
  text-align: center;
  line-height: 1.3;
  font-size: 13px;
}

/* ── Typing indicator ── */
.chat-typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
}
.chat-typing span {
  width: 7px;
  height: 7px;
  background-color: #9e9e9e;
  border-radius: 50%;
  animation: chat-pulse 1.4s infinite ease-in-out;
}
.chat-typing span:nth-child(1) { animation-delay: 0s; }
.chat-typing span:nth-child(2) { animation-delay: 0.2s; }
.chat-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes chat-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1); }
}

/* ── Error state ── */
.chat-error {
  background: #fef2f2;
  border-radius: 12px;
  padding: 14px 16px;
  text-align: center;
}
.chat-error-text {
  font-size: 13px;
  color: #c62828;
  line-height: 1.4;
}

/* ── Input bar ── */
.chat-input-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 10px 16px calc(16px + env(safe-area-inset-bottom));
  padding: 10px 16px calc(16px + constant(safe-area-inset-bottom));
  background: linear-gradient(to top, rgba(245, 246, 250, 0.95) 60%, rgba(245, 246, 250, 0));
}
.chat-input-wrapper {
  max-width: 968px;
  margin: 0 auto;
}
.chat-input-card {
  background: white;
  border-radius: 22px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.chat-input .v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
  padding: 8px 8px 8px 18px !important;
  min-height: 46px !important;
  align-items: flex-end !important;
}
.chat-input .v-text-field__slot {
  margin: 0 !important;
}
.chat-input .v-text-field__slot textarea {
  padding: 6px 0 !important;
  line-height: 1.4 !important;
  font-size: 14px !important;
}
.chat-input .v-input__append-inner {
  background-color: transparent;
  padding: 0;
  margin: 0 4px 6px 0;
  align-self: flex-end;
}
.chat-send-btn {
  transition: transform 0.15s ease, opacity 0.15s ease !important;
}
.chat-send-btn:hover:not(.v-btn--disabled) {
  transform: scale(1.08);
}

/* ── Markdown styles ── */
.chat-markdown {
  font-size: 14px;
  line-height: 1.6;
  color: #212121;
}
.chat-markdown p {
  margin-bottom: 8px;
}
.chat-markdown p:last-child {
  margin-bottom: 0;
}
.chat-markdown h1,
.chat-markdown h2,
.chat-markdown h3 {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #193153;
}
.chat-markdown h1 { font-size: 1.3em; }
.chat-markdown h2 { font-size: 1.15em; }
.chat-markdown h3 { font-size: 1.05em; }
.chat-markdown code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
  color: #c62828;
}
.chat-markdown pre {
  background: #f5f5f5;
  padding: 12px 16px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
}
.chat-markdown pre code {
  background: none;
  padding: 0;
  color: inherit;
}
.chat-markdown ul,
.chat-markdown ol {
  padding-left: 20px;
  margin-bottom: 8px;
}
.chat-markdown li {
  margin-bottom: 4px;
}
.chat-markdown blockquote {
  border-left: 3px solid #193153;
  margin: 8px 0;
  padding: 4px 12px;
  color: #546e7a;
}
.chat-markdown a {
  color: #00728a;
  text-decoration: none;
}
.chat-markdown a:hover {
  text-decoration: underline;
}
.chat-markdown a[href^="/"] {
  display: inline-block;
  background-color: rgba(25, 49, 83, 0.08);
  color: #193153;
  padding: 2px 10px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 13px;
  text-decoration: none;
}
.chat-markdown a[href^="/"]:hover {
  background-color: rgba(25, 49, 83, 0.15);
  text-decoration: none;
}
.chat-markdown a[href^="http"] {
  display: inline-block;
  background-color: rgba(0, 114, 138, 0.08);
  color: #00728a;
  padding: 2px 10px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 13px;
  text-decoration: none;
}
.chat-markdown a[href^="http"]:hover {
  background-color: rgba(0, 114, 138, 0.18);
  text-decoration: none;
}
</style>

<script>
import { mdiArrowUp, mdiCreation, mdiChevronLeft, mdiChevronDown, mdiContentCopy, mdiWeb, mdiFileDocumentOutline, mdiFilePdfBox, mdiImageOutline, mdiFileTableOutline } from "@mdi/js";
import { mapGetters, mapActions } from "vuex";
import VueMarkdown from "vue-markdown";
import { transformCitations } from "@/constants/citationRoutes";
import AiAttachmentPicker from "@/components/AiAttachmentPicker.vue";

export default {
  name: "AIConversation",
  components: {
    "vue-markdown": VueMarkdown,
    "ai-attachment-picker": AiAttachmentPicker,
  },
  directives: {
    openLinksInNewTab: {
      inserted(el, binding) {
        el.__linkClickHandler = function (event) {
          var link = event.target.closest("a");
          if (link) {
            event.preventDefault();
            var url = link.getAttribute("href");
            if (typeof binding.value === "function") {
              binding.value(url);
            }
          }
        };
        el.addEventListener("click", el.__linkClickHandler);
      },
      unbind(el) {
        if (el.__linkClickHandler) {
          el.removeEventListener("click", el.__linkClickHandler);
          delete el.__linkClickHandler;
        }
      },
    },
  },
  data() {
    return {
      textModel: "",
      sending: false,
      initialLoading: false,
      copySnackbar: false,
      showScrollBottom: false,
      refreshing: false,
      files: [],
      lastUserFiles: [],
      mdiArrowUp,
      mdiCreation,
      mdiChevronLeft,
      mdiChevronDown,
      mdiContentCopy,
      mdiWeb,
      pollingInterval: null,
      conversationId: null,
      lastUserMessage: null,
      // Pull to refresh
      pullStartY: 0,
      pullDistance: 0,
      isPulling: false,
      suggestions: [
        "Quais sinais de parvovirose em caes?",
        "Interacao entre Meloxicam e Prednisolona em felinos?",
        "Protocolo de fluidoterapia em gatos desidratados",
      ],
    };
  },
  computed: {
    ...mapGetters("topics", ["conversation"]),
    isNew() {
      return this.$route.params.id === "new";
    },
    conversationTitle() {
      if (this.isNew && !this.conversation) return "Nova Conversa";
      if (this.conversation && this.conversation.messages && this.conversation.messages.length) {
        var firstUserMsg = this.conversation.messages.find(function(m) { return m.role === "user"; });
        if (firstUserMsg && firstUserMsg.content) {
          return firstUserMsg.content.substring(0, 50);
        }
      }
      return "Conversa";
    },
    messages() {
      if (!this.conversation || !this.conversation.messages) return [];
      return this.conversation.messages;
    },
    isProcessing() {
      return this.conversation && this.conversation.status === "processing";
    },
    hasError() {
      return this.conversation && this.conversation.status === "error";
    },
    errorMessage() {
      return this.conversation && this.conversation.error_message;
    },
    canSend() {
      if (this.isProcessing || this.sending) return false;
      const hasText = !!(this.textModel && this.textModel.trim());
      const hasFiles = Array.isArray(this.files) && this.files.length > 0;
      return hasText || hasFiles;
    },
  },
  async mounted() {
    this.clearConversation();
    if (!this.isNew) {
      this.conversationId = this.$route.params.id;
      this.initialLoading = true;
      await this.openConversation({ id: this.conversationId });
      this.initialLoading = false;
      this.setupScreenSizes();
      this.scrollToBottom();
      if (this.isProcessing) {
        this.startPolling();
      }
    } else {
      this.setupScreenSizes();
    }
    window.addEventListener("scroll", this.onScroll);
  },
  beforeDestroy() {
    this.stopPolling();
    this.clearConversation();
    window.removeEventListener("scroll", this.onScroll);
  },
  watch: {
    isProcessing(val) {
      if (val) {
        this.startPolling();
      } else {
        this.stopPolling();
        this.scrollToBottom();
      }
    },
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
  },
  methods: {
    ...mapActions("general", ["openExternalUrl", "routePathRedirect", "routeBack"]),
    ...mapActions("topics", [
      "openConversation",
      "sendMessage",
      "pollConversation",
    ]),
    clearConversation() {
      this.$store.commit("topics/clearConversation");
    },
    handleLinkClick(url) {
      if (url && url.startsWith("/")) {
        this.routePathRedirect({ path: url });
      } else {
        this.openExternalUrl({ url, inAppBrowser: true });
      }
    },
    getProcessedContent(msg) {
      var content = msg.content || "";

      content = content.replace(/\b(?:cite)?(?:turn\d+(?:file|search)\d+)+\b/g, '');
      content = content.replace(/\?utm_soa:[^\s)\]]+/g, '');
      content = content.replace(/(\[[^\]]+\]\([^)\]]+)\]\)/g, '$1)');
      content = content.replace(/(\[[^\]]+\]\([^)\]]+)\](?=[\s.,;:!?)]|$)/g, '$1)');

      var citations = msg.citations || [];
      var fileMap = {};
      for (var i = 0; i < citations.length; i++) {
        var c = citations[i];
        if (c.file_id && c.filename) {
          fileMap[c.file_id] = c.filename;
        }
      }

      content = content.replace(/\[VetAlfa:(file-[^\]]+)\]/gi, function(_, fileId) {
        var filename = fileMap[fileId.trim()];
        return filename ? "[VetAlfa:" + filename + "]" : "";
      });

      content = transformCitations(content);
      content = content.replace(/\[VetAlfa:[^\]]*\]/gi, "");
      content = content.replace(/ {2,}/g, " ").replace(/\n{3,}/g, "\n\n");

      return content;
    },
    fillSuggestion(text) {
      this.textModel = text;
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },

    // Copy to clipboard
    async copyMessage(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.copySnackbar = true;
      } catch {
        // silent fail
      }
    },

    // Scroll to bottom detection
    onScroll() {
      const scrollBottom =
        document.documentElement.scrollHeight -
        window.innerHeight -
        window.scrollY;
      this.showScrollBottom = scrollBottom > 200;
    },

    // Pull to refresh
    onPullStart(e) {
      if (window.scrollY <= 0) {
        this.pullStartY = e.touches[0].clientY;
        this.isPulling = true;
      }
    },
    onPullMove(e) {
      if (!this.isPulling || this.refreshing) return;
      if (window.scrollY > 0) {
        this.pullDistance = 0;
        return;
      }
      const dy = e.touches[0].clientY - this.pullStartY;
      if (dy > 0) {
        this.pullDistance = Math.round(dy * 0.4);
      }
    },
    async onPullEnd() {
      if (!this.isPulling) return;
      this.isPulling = false;
      if (this.pullDistance > 60 && this.conversationId) {
        this.refreshing = true;
        await this.openConversation({ id: this.conversationId });
        this.refreshing = false;
      }
      this.pullDistance = 0;
    },

    iconForMime(mime) {
      if (!mime) return mdiFileDocumentOutline;
      if (mime.indexOf("image/") === 0) return mdiImageOutline;
      if (mime === "application/pdf") return mdiFilePdfBox;
      if (mime === "text/csv") return mdiFileTableOutline;
      return mdiFileDocumentOutline;
    },
    async sendMsg() {
      if (!this.canSend) return;
      const message = (this.textModel && this.textModel.trim()) || "";
      const filesToSend = Array.isArray(this.files) ? this.files.slice() : [];
      if (!message && !filesToSend.length) return;

      this.lastUserMessage = message;
      this.lastUserFiles = filesToSend.slice();
      this.textModel = "";
      this.files = [];
      this.sending = true;

      const result = await this.sendMessage({
        conversationId: this.conversationId,
        message,
        files: filesToSend,
      });

      this.sending = false;

      if (result && result.conversationId) {
        if (this.isNew || !this.conversationId) {
          this.conversationId = result.conversationId;
          this.$store.dispatch("general/pushOrPopRouteOfBreadcrumb", { offset: 1 });
          this.$router.replace({
            path: `/ai-conversations/${result.conversationId}`,
          });
        }
        this.startPolling();
      }

      this.scrollToBottom();
    },
    async retryLastMessage() {
      if (!this.lastUserMessage && !(this.lastUserFiles && this.lastUserFiles.length)) return;
      this.textModel = this.lastUserMessage || "";
      this.files = Array.isArray(this.lastUserFiles) ? this.lastUserFiles.slice() : [];
      await this.sendMsg();
    },
    startPolling() {
      this.stopPolling();
      this.pollNow();
      this.pollingInterval = setInterval(() => {
        this.pollNow();
      }, 5000);
    },
    async pollNow() {
      if (!this.conversationId) return;
      const data = await this.pollConversation({
        id: this.conversationId,
      });
      if (data && data.status !== "processing") {
        this.stopPolling();
      }
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesEnd) {
          this.$refs.messagesEnd.scrollIntoView({ behavior: "smooth" });
        }
      });
    },
    setupScreenSizes() {
      this._setupScreenSizes(this);
    },
  },
};
</script>
